import { type ReactNode, useEffect } from 'react';
import type { GuestTheme } from '../../../modules/interfaces';
import { buildThemeCss } from '../../../modules/utils/guestTheme';

const STYLE_ELEMENT_ID = 'guest-portal-theme';

interface GuestThemeProviderProps {
  /**
   * The active theme for the property (server-resolved). When undefined, no overrides
   * are injected and the guest portal renders its built-in default look.
   */
  theme?: GuestTheme;
  children: ReactNode;
}

/**
 * Applies a property's active guest-portal theme at runtime.
 *
 * The portal's stylesheets are already driven by `#guest-portal` CSS custom properties
 * (see `src/index.css`). This provider injects a single `<style id="guest-portal-theme">`
 * element that re-declares those variables for the `#guest-portal` scope from the resolved
 * theme. Because the layout root and every guest modal carry `id="guest-portal"`, the
 * override reaches all of them — including modals portaled to `document.body`. Later source
 * order beats the equal-specificity base block in `index.css`.
 *
 * When `theme` is undefined the element is removed entirely, so nothing is overridden and
 * the default palette applies.
 */
export const GuestThemeProvider = ({ theme, children }: GuestThemeProviderProps) => {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ELEMENT_ID);

    if (!theme) {
      existing?.remove();
      document.body.style.removeProperty('background-color');
      return;
    }

    const styleElement =
      (existing as HTMLStyleElement | null) ?? document.createElement('style');
    styleElement.id = STYLE_ELEMENT_ID;
    styleElement.textContent = buildThemeCss(theme);

    if (!existing) {
      document.head.appendChild(styleElement);
    }

    // The portal renders as a centered column; the gutters beside it are `<body>`, which
    // lives outside the `#guest-portal` scope and so can't read the themed background var.
    // Paint the page background directly so those gutters follow the theme too. `body` in
    // index.css sets its background with `!important`, so match that priority to win.
    document.body.style.setProperty(
      'background-color',
      theme.colors.bodyBackground,
      'important',
    );

    return () => {
      document.getElementById(STYLE_ELEMENT_ID)?.remove();
      document.body.style.removeProperty('background-color');
    };
  }, [theme]);

  return <>{children}</>;
};
